import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { LogOut, Clock, CheckCircle, XCircle, ArrowRight, Plus, CalendarPlus, Users, Wrench, Camera, Image } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { key: "Inskickat", label: "Inkomna", icon: Clock },
  { key: "Publicerat", label: "Publicerade", icon: CheckCircle },
  { key: "Avvisat", label: "Avvisade", icon: XCircle },
  { key: "Bilder", label: "Bilder", icon: Image },
];

function modHeaders() {
  return { Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` };
}

export default function ModDashboard() {
  const [tab, setTab] = useState("Inskickat");
  const [anslag, setAnslag] = useState([]);
  const [bilder, setBilder] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = JSON.parse(sessionStorage.getItem("mod_user") || "null");

  const fetchAnslag = useCallback(async (status) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mod/anslag`, { params: { status }, headers: modHeaders() });
      setAnslag(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/mod/login");
    }
    setLoading(false);
  }, [navigate]);

  const fetchBilder = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/mod/bilder`, { headers: modHeaders() });
      setBilder(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/mod/login");
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!sessionStorage.getItem("mod_token")) { navigate("/mod/login"); return; }
    if (tab === "Bilder") {
      fetchBilder();
    } else {
      fetchAnslag(tab);
    }
  }, [tab, navigate, fetchAnslag, fetchBilder]);

  const logout = () => {
    sessionStorage.removeItem("mod_token");
    sessionStorage.removeItem("mod_user");
    navigate("/mod/login");
  };

  return (
    <div data-testid="mod-dashboard" className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-2xl font-medium text-ink">Moderatorvy</h1>
          <p className="text-sm text-muted-foreground">Inloggad som {user?.name || user?.email}</p>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/mod/skapa" data-testid="mod-create-btn" className="inline-flex items-center gap-2 bg-pine hover:bg-pine-hover text-white font-medium text-sm px-4 py-2.5 rounded-md transition-colors no-underline">
            <Plus size={16} /> Skapa anslag
          </Link>
          <Link to="/mod/skapa-evenemang" data-testid="mod-create-event-btn" className="inline-flex items-center gap-2 border-2 border-pine text-pine hover:bg-bone-alt font-medium text-sm px-4 py-2.5 rounded-md transition-colors no-underline">
            <CalendarPlus size={16} /> Skapa evenemang
          </Link>
          <Link to="/mod/foreningar" data-testid="mod-foreningar-btn" className="inline-flex items-center gap-2 border-2 border-pine text-pine hover:bg-bone-alt font-medium text-sm px-4 py-2.5 rounded-md transition-colors no-underline">
            <Users size={16} /> Föreningar
          </Link>
          <Link to="/mod/praktiskt" data-testid="mod-praktiskt-btn" className="inline-flex items-center gap-2 border-2 border-pine text-pine hover:bg-bone-alt font-medium text-sm px-4 py-2.5 rounded-md transition-colors no-underline">
            <Wrench size={16} /> Praktiskt
          </Link>
          <button onClick={logout} data-testid="mod-logout" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-falu transition-colors">
            <LogOut size={16} /> Logga ut
          </button>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-warm-border">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            data-testid={`mod-tab-${t.key.toLowerCase()}`}
            className={`inline-flex items-center gap-2 px-4 py-3 text-base font-medium border-b-2 transition-colors ${
              tab === t.key ? "border-pine text-pine" : "border-transparent text-muted-foreground hover:text-ink"
            }`}
          >
            <t.icon size={18} /> {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-muted-foreground py-8">Laddar...</p>
      ) : tab === "Bilder" ? (
        <BilderTab bilder={bilder} onRefresh={fetchBilder} />
      ) : anslag.length === 0 ? (
        <div data-testid="mod-empty" className="bg-bone-alt rounded-sm p-8 text-center">
          <p className="text-muted-foreground">Inga anslag med status &ldquo;{TABS.find(t => t.key === tab)?.label}&rdquo;.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {anslag.map((a) => (
            <Link
              key={a.id}
              to={`/mod/anslag/${a.id}`}
              data-testid={`mod-anslag-${a.id}`}
              className="flex items-center justify-between bg-white border border-warm-border rounded-sm p-4 hover:bg-bone-alt/50 transition-colors no-underline group"
            >
              <div className="flex-1 min-w-0">
                <p className="text-sm text-muted-foreground mb-1">{a.rubrik} &middot; {a.avsandare_namn}</p>
                <h3 className="font-heading text-base font-medium text-ink group-hover:text-falu transition-colors truncate">{a.titel}</h3>
                <p className="text-sm text-muted-foreground mt-1">{new Date(a.inskicksdatum).toLocaleDateString("sv-SE")}</p>
              </div>
              <ArrowRight size={18} className="text-muted-foreground ml-3 flex-shrink-0" />
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}


function BilderTab({ bilder, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const handleAction = async (id, action) => {
    setBusy(true);
    try {
      await axios.post(`${API}/mod/bilder/${id}/${action}`, {}, { headers: modHeaders() });
      onRefresh();
    } catch {}
    setBusy(false);
  };

  const inskickade = bilder.filter(b => b.status === "Inskickat");
  const publicerade = bilder.filter(b => b.status === "Publicerat");

  return (
    <div data-testid="mod-bilder-tab">
      <h2 className="font-heading text-lg font-medium text-ink mb-4">Inkomna bilder ({inskickade.length})</h2>
      {inskickade.length === 0 && <p className="text-muted-foreground mb-8">Inga inkomna bilder att granska.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10">
        {inskickade.map((b) => (
          <div key={b.id} data-testid={`mod-bild-${b.id}`} className="bg-white border border-warm-border rounded-lg overflow-hidden">
            <img
              src={b.bild_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${b.bild_url}` : b.bild_url}
              alt={b.titel}
              className="w-full h-48 object-cover"
            />
            <div className="p-4">
              <h3 className="font-heading text-base font-medium text-ink mb-1">{b.titel}</h3>
              <p className="text-sm text-muted-foreground mb-1">Foto: {b.fotograf}{b.ort ? ` · ${b.ort}` : ""}</p>
              {b.beskrivning && <p className="text-sm text-muted-foreground mb-2">{b.beskrivning}</p>}
              {b.tema && <span className="inline-block text-xs bg-pine/10 text-pine px-2 py-0.5 rounded mb-2">{b.tema}</span>}
              <div className="flex gap-2 mt-2">
                <button
                  disabled={busy}
                  onClick={() => handleAction(b.id, "publish")}
                  data-testid={`publish-bild-${b.id}`}
                  className="flex-1 bg-pine hover:bg-pine-hover disabled:opacity-50 text-white text-sm font-medium py-2 rounded transition-colors"
                >
                  Publicera
                </button>
                <button
                  disabled={busy}
                  onClick={() => handleAction(b.id, "reject")}
                  data-testid={`reject-bild-${b.id}`}
                  className="flex-1 bg-falu hover:bg-falu-hover disabled:opacity-50 text-white text-sm font-medium py-2 rounded transition-colors"
                >
                  Avvisa
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <h2 className="font-heading text-lg font-medium text-ink mb-4">Publicerade bilder ({publicerade.length})</h2>
      {publicerade.length === 0 && <p className="text-muted-foreground">Inga publicerade bilder ännu.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {publicerade.map((b) => (
          <div key={b.id} className="bg-white border border-warm-border rounded-lg overflow-hidden">
            <img
              src={b.bild_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${b.bild_url}` : b.bild_url}
              alt={b.titel}
              className="w-full h-32 object-cover"
            />
            <div className="p-3">
              <p className="text-sm font-medium text-ink truncate">{b.titel}</p>
              <p className="text-xs text-muted-foreground">{b.fotograf}</p>
              <button
                disabled={busy}
                onClick={() => handleAction(b.id, "unpublish")}
                className="mt-2 text-xs text-falu hover:text-falu-hover"
              >
                Avpublicera
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


